import moment from 'moment';
import { isoDateFormat } from './time-utils.js';
import { Item } from './item.js';
import { getReversedAncestors } from './ordered-relation.js';
import PouchDB from 'pouchdb';

export const colorArray = [
  [
    'undefined',
    'lightred',
    'lightorange',
  ],
  [
    'lightyellow',
    'lightgreen',
    'lightcyan',
  ],
  [
    'lightblue',
    'lightviolet',
    'lightpink',
  ],
  [
    'lightgrey',
    'red',
    'green',
  ],
  [
    'yellow',
    'blue',
    'violet',
  ]
]

export const labelColorArray = [
  [
    'undefined',
    'darkgrey',
  ],
  [
    'red',
    'orange',
  ],
  [
    'yellow',
    'green',
  ],
  [
    'cyan',
    'blue',
  ],
  [
    'violet',
    'pink',
  ]
]

const now = moment();
const todayIso = now.clone().startOf('day').format(isoDateFormat);
const thisWeekIso = now.clone().startOf('isoWeek').format(isoDateFormat);
const thisMonthIso = now.clone().startOf('month').format(isoDateFormat);

// Check for various conditions that should result in item not being shown.
export function calcVisibility(item, vue) {
  if (!!item.archivedTimestampMs && !vue.$root.showArchived) {
    return false;
  }
  if (!!item.doneAtTimestampMs && !vue.$root.showDone) {
    return false;
  }

  const ancestors = getReversedAncestors(item.cache, item)
    .map(rel => rel.subtaskOf)
    .map(pptr => pptr.parentKey)
    .map(id => item.cache.docs[id])

  const ancestorAssignees = [].concat.apply(
    [],
    ancestors.filter(item => !!item.assignees).map(item => item.assignees)
  );

  // Manage filtering by labels (deep search).
  function elementsAssignees(item) {
    const result = []
    if (item.assignees) {
      result.push(...item.assignees)
    }
    for (const elem of item.getChildren()) {
      result.push(...elementsAssignees(elem));
    }
    return result;
  }
  const combinedAssignees = [
    ...ancestorAssignees,
    ...elementsAssignees(item),
  ]
  if (item.assignees) {
    combinedAssignees.push(...item.assignees)
  }

  // Hide items that are assigned to someone other than me.
  if (!vue.$root.showNotMine &&
    combinedAssignees.length > 0 &&
    !combinedAssignees.includes(vue.$sessionState.loggedInAs)) {
    return false;
  }

  // Manage filtering by labels (deep search).
  function hasSelectedLabels(item) {
    if (item.labels) {
      for (const id of item.labels) {
        if (vue.$root.selectedLabels.includes(id)) return true;
      }
    }
    for (const elem of item.getChildren()) {
      if (hasSelectedLabels(elem)) return true;
    }
    return false;
  }

  if (vue.$root.showOnlyCurrent && item.plannedFor) {
    if (item.plannedFor.parentClass === 'list') {
      return false;
    }
    if (item.plannedFor.parentClass === 'month' &&
      item.plannedFor.parentKey !== thisMonthIso) {
      return false;
    }
    if (item.plannedFor.parentClass === 'week' &&
      item.plannedFor.parentKey !== thisWeekIso) {
      return false;
    }
    if (item.plannedFor.parentClass === 'day' &&
      item.plannedFor.parentKey !== todayIso) {
      return false;
    }
  }

  if (!vue.$root.selectedLabels.length) {
    return true
  } else if (hasSelectedLabels(item)) {
    return true;
  }
  return false;
}

export function countDone(tasks) {
  return tasks.filter(
    (task) => task.type === 'task' && task.doneAtTimestampMs).length;
}

export function progressPercentsFromSubtasks(subtasks) {
  const actualTasks = subtasks.filter((task) => task.type === 'task');
  const subtasksCount = actualTasks.length;
  if (!subtasksCount) {
    return 0;
  }
  return 100 * countDone(actualTasks) / subtasksCount;
}

export function upFromTaskView(vue) {
  if ('taskId' in vue.$route.params) {
    const item = vue.$reactiveCache.docs[vue.$route.params.taskId]
    let link;
    if (isSubtask(item)) {
      link = `/task/${item.subtaskOf.parentKey}`
    } else {
      if (vue.$root.lastDashboard) {
        link = vue.$root.lastDashboard
      } else {
        let panelName;
        switch (item.plannedFor.parentClass) {
          case 'day':
            panelName = 'days'; break;
          case 'week':
            panelName = 'weeks'; break;
          case 'month':
            panelName = 'months'; break;
          case 'backlog':
            panelName = 'backlog'; break;
        }
        link = `/calendar#${panelName}`;
      }
    }
    vue.$router.push(link)
  } else {
    console.error("Not in task view, cannot go back")
  }
}

export function calcItemColor(item, reactiveCache) {
  if (!item) return undefined;
  if (!!item.color) return item.color;
  if (item.subtaskOf) {
    const parent = reactiveCache.docs[item.subtaskOf.parentKey];
    if (parent) return calcItemColor(parent, reactiveCache);
  }
  return undefined;
}

export function isSubtask(item) {
  return item.subtaskOf && item.subtaskOf.parentClass === "task";
}

export function downloadAsJson(data, filename) {
  const serializedData = JSON.stringify(data, null, 2);
  const blob = new Blob([serializedData], { type: 'text/plain' });
  const link = document.createElement('a');
  link.download = filename + ".json";
  link.href = window.URL.createObjectURL(blob);
  link.click();
}

export function fromMatchingDb(items, couch) {
  return items.filter(item => item.couch === couch);
}

export function removeFromAllLayouts(id, cache) {
  for (const layoutId of Object.keys(cache.layouts)) {
    const layoutDoc = cache.docs[layoutId]
    if (!layoutDoc) {
      console.error(`Invalid layout ID: "${layoutId}"`)
    } else {
      if (layoutDoc.layout.includes(id)) {
        const index = layoutDoc.layout.indexOf(id);
        layoutDoc.layout.splice(index, 1)
        layoutDoc.save()
      }
    }
  }
}
